import { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../../services/api';
import { connectSocket } from '../../services/realtime/socket';

export default function useSessionQna(sessionId, { onNewQuestion, onNewReply } = {}) {
  const onNewQuestionRef = useRef(onNewQuestion);
  onNewQuestionRef.current = onNewQuestion;
  const onNewReplyRef = useRef(onNewReply);
  onNewReplyRef.current = onNewReply;
  // Used to keep a poster from being notified about their own question (the
  // live echo can't be attributed to them when it was posted anonymously).
  const pendingPostsRef = useRef(0);
  const ownQuestionIdsRef = useRef(new Set());
  const [questions, setQuestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [posting, setPosting] = useState(false);
  const joinedRef = useRef(false);

  const load = useCallback(async () => {
    if (!sessionId) return;
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getSessionQna(sessionId);
      if (response.success) {
        setQuestions(response.questions || []);
        setIsOpen(Boolean(response.isOpen));
        setSessionInfo(response.session || null);
      } else {
        setError(response.message || 'Failed to load Q&A');
      }
    } catch (e) {
      setError(e.message || 'Failed to load Q&A');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const upsertQuestion = useCallback((question) => {
    setQuestions((prev) => (
      prev.some((q) => q.id === question.id)
        // Socket echo can land before the POST response; keep isMine from the response.
        ? prev.map((q) => (q.id === question.id && question.isMine ? { ...q, isMine: true } : q))
        : [...prev, question]
    ));
  }, []);

  const upsertReply = useCallback((reply) => {
    setQuestions((prev) => prev.map((q) => {
      if (q.id !== reply.questionId) return q;
      if (q.replies.some((r) => r.id === reply.id)) return q;
      return { ...q, replies: [...q.replies, reply] };
    }));
  }, []);

  const applyReactionCounts = useCallback((targetType, targetId, reactions, myReaction) => {
    setQuestions((prev) => prev.map((q) => {
      if (targetType === 'questions') {
        if (q.id !== targetId) return q;
        return { ...q, reactions, ...(myReaction !== undefined ? { myReaction } : {}) };
      }
      return {
        ...q,
        replies: q.replies.map((r) => (
          r.id === targetId ? { ...r, reactions, ...(myReaction !== undefined ? { myReaction } : {}) } : r
        )),
      };
    }));
  }, []);

  const applyEdit = useCallback((targetType, targetId, body, editedAt) => {
    setQuestions((prev) => prev.map((q) => {
      if (targetType === 'questions') {
        return q.id === targetId ? { ...q, body, editedAt } : q;
      }
      return { ...q, replies: q.replies.map((r) => (r.id === targetId ? { ...r, body, editedAt } : r)) };
    }));
  }, []);

  const applyDelete = useCallback((targetType, targetId) => {
    setQuestions((prev) => (
      targetType === 'questions'
        ? prev.filter((q) => q.id !== targetId)
        : prev.map((q) => ({ ...q, replies: q.replies.filter((r) => r.id !== targetId) }))
    ));
  }, []);

  useEffect(() => {
    if (!sessionId) return undefined;
    load();

    const socket = connectSocket();

    const handleQuestionNew = (question) => {
      if (question.sessionId !== Number(sessionId)) return;
      upsertQuestion(question);
      const isOwnEcho = pendingPostsRef.current > 0 || ownQuestionIdsRef.current.has(question.id);
      if (!isOwnEcho && onNewQuestionRef.current) onNewQuestionRef.current(question);
    };
    const handleReplyNew = (reply) => {
      upsertReply(reply);
      if (onNewReplyRef.current) onNewReplyRef.current(reply);
    };
    const handleReactionUpdate = ({ sessionId: sid, targetType, targetId, reactions }) => {
      if (sid !== Number(sessionId)) return;
      applyReactionCounts(targetType, targetId, reactions, undefined);
    };

    const handleEdited = ({ sessionId: sid, targetType, targetId, body, editedAt }) => {
      if (sid !== Number(sessionId)) return;
      applyEdit(targetType, targetId, body, editedAt);
    };
    const handleDeleted = ({ sessionId: sid, targetType, targetId }) => {
      if (sid !== Number(sessionId)) return;
      applyDelete(targetType, targetId);
    };

    const join = () => {
      socket.emit('qna:join', { sessionId }, (ack) => {
        if (!ack?.success) {
          setError(ack?.message || 'Could not join live Q&A');
          return;
        }
        joinedRef.current = true;
      });
    };

    socket.on('qna:question:new', handleQuestionNew);
    socket.on('qna:reply:new', handleReplyNew);
    socket.on('qna:reaction:update', handleReactionUpdate);
    socket.on('qna:message:edited', handleEdited);
    socket.on('qna:message:deleted', handleDeleted);
    socket.on('connect', join);
    if (socket.connected) join();

    return () => {
      socket.off('qna:question:new', handleQuestionNew);
      socket.off('qna:reply:new', handleReplyNew);
      socket.off('qna:reaction:update', handleReactionUpdate);
      socket.off('qna:message:edited', handleEdited);
      socket.off('qna:message:deleted', handleDeleted);
      socket.off('connect', join);
      if (joinedRef.current) {
        socket.emit('qna:leave', { sessionId });
        joinedRef.current = false;
      }
    };
  }, [sessionId, load, upsertQuestion, upsertReply, applyReactionCounts, applyEdit, applyDelete]);

  const postQuestion = useCallback(async (body, isAnonymous = false) => {
    const trimmed = body.trim();
    if (!trimmed) return;
    try {
      setPosting(true);
      setError('');
      pendingPostsRef.current += 1;
      const response = await apiService.postSessionQuestion(sessionId, trimmed, isAnonymous);
      if (response.success) {
        ownQuestionIdsRef.current.add(response.question.id);
        upsertQuestion(response.question);
      } else {
        setError(response.message || 'Failed to post question');
      }
    } catch (e) {
      setError(e.message || 'Failed to post question');
    } finally {
      pendingPostsRef.current = Math.max(0, pendingPostsRef.current - 1);
      setPosting(false);
    }
  }, [sessionId, upsertQuestion]);

  const postReply = useCallback(async (questionId, body) => {
    const trimmed = body.trim();
    if (!trimmed) return;
    try {
      setPosting(true);
      setError('');
      const response = await apiService.postSessionQuestionReply(sessionId, questionId, trimmed);
      if (response.success) {
        upsertReply(response.reply);
      } else {
        setError(response.message || 'Failed to post reply');
      }
    } catch (e) {
      setError(e.message || 'Failed to post reply');
    } finally {
      setPosting(false);
    }
  }, [sessionId, upsertReply]);

  const toggleReaction = useCallback(async (targetType, targetId, reactionType) => {
    try {
      const response = await apiService.toggleSessionQnaReaction(sessionId, targetType, targetId, reactionType);
      if (response.success) {
        applyReactionCounts(targetType, targetId, response.reactions, response.myReaction);
      }
    } catch (e) {
      setError(e.message || 'Failed to update reaction');
    }
  }, [sessionId, applyReactionCounts]);

  const editMessage = useCallback(async (targetType, targetId, body) => {
    const trimmed = body.trim();
    if (!trimmed) return false;
    try {
      setError('');
      const response = await apiService.editSessionQnaMessage(sessionId, targetType, targetId, trimmed);
      if (response.success) {
        applyEdit(targetType, targetId, response.body, response.editedAt);
        return true;
      }
      setError(response.message || 'Failed to update message');
    } catch (e) {
      setError(e.message || 'Failed to update message');
    }
    return false;
  }, [sessionId, applyEdit]);

  const deleteMessage = useCallback(async (targetType, targetId) => {
    try {
      setError('');
      const response = await apiService.deleteSessionQnaMessage(sessionId, targetType, targetId);
      if (response.success) {
        applyDelete(targetType, targetId);
        return true;
      }
      setError(response.message || 'Failed to delete message');
    } catch (e) {
      setError(e.message || 'Failed to delete message');
    }
    return false;
  }, [sessionId, applyDelete]);

  return { editMessage, deleteMessage, questions, isOpen, loading, error, posting, postQuestion, postReply, toggleReaction, sessionInfo, reload: load };
}
